import { useState, useEffect } from 'react';
import { Save, Mail, Server, Shield, Info, Send, CheckCircle, XCircle } from 'lucide-react';
import { settingsApi } from '@/api/client';
import type { SystemConfig } from '@/types';

export default function Settings() {
  const [config, setConfig] = useState<SystemConfig>({
    smtp_host: '', smtp_port: '', smtp_user: '', smtp_password: '', app_version: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // 邮件测试
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await settingsApi.get();
      if (response.success) setConfig(response.data);
    } catch { console.error('Failed to fetch config'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsApi.update(config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('保存失败'); }
    finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { alert('请输入测试邮箱地址'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/test-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ to: testEmail }),
      });
      const data = await response.json() as { success: boolean; message: string };
      setTestResult({ success: data.success, message: data.message });
    } catch {
      setTestResult({ success: false, message: '请求失败，请检查网络' });
    } finally {
      setTesting(false);
    }
  };

  const inputFields = [
    { key: 'smtp_host', label: 'SMTP服务器地址', placeholder: '如：smtp.163.com 或 smtp.126.com', icon: Server },
    { key: 'smtp_port', label: 'SMTP端口', placeholder: '465（SSL）或 25（非SSL）', icon: Server },
    { key: 'smtp_user', label: 'SMTP登录账号（邮箱地址）', placeholder: '如：yourname@163.com', icon: Mail },
    { key: 'smtp_password', label: 'SMTP授权码（非邮箱登录密码）', placeholder: '请在邮箱设置中开启SMTP服务获取授权码', icon: Shield, type: 'password' },
    { key: 'app_version', label: '应用版本号', placeholder: '1.0.0', icon: Info },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">系统设置</h1>
        <p className="text-slate-400 mt-1">配置SMTP服务器和系统参数</p>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-6">
        <h2 className="text-lg font-semibold text-white mb-6">SMTP配置</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {inputFields.map((field) => {
            const Icon = field.icon;
            return (
              <div key={field.key}>
                <label className="block text-sm font-medium text-slate-300 mb-2">{field.label}</label>
                <div className="relative">
                  <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type={field.type || 'text'}
                    value={config[field.key as keyof SystemConfig]}
                    onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            );
          })}

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-6">
            <h3 className="text-sm font-semibold text-blue-400 mb-2">配置说明</h3>
            <ul className="text-slate-400 text-sm space-y-1">
              <li>- <span className="text-yellow-300">SMTP授权码≠邮箱登录密码</span>，需在邮箱设置→POP3/SMTP/IMAP中开启SMTP服务后获取</li>
              <li>- 163邮箱：服务器 smtp.163.com，端口465（SSL），需先开启SMTP服务获取授权码</li>
              <li>- 126邮箱：服务器 smtp.126.com，端口465（SSL），需先开启SMTP服务获取授权码</li>
              <li>- QQ邮箱：服务器 smtp.qq.com，端口465（SSL），需先开启SMTP服务获取授权码</li>
              <li>- Gmail：服务器 smtp.gmail.com，端口465（SSL），需开启应用专用密码</li>
              <li>- <span className="text-green-400">配置会立即生效，无需重启</span>。更换邮箱后直接修改保存即可</li>
            </ul>
          </div>

          <div className="flex gap-3 pt-6">
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all disabled:opacity-50">
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                  保存中...
                </span>
              ) : saved ? (
                <span className="flex items-center gap-2"><Save className="w-4 h-4" />已保存</span>
              ) : (
                <span className="flex items-center gap-2"><Save className="w-4 h-4" />保存设置</span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* 邮件测试 */}
      <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">邮件测试</h2>
        <p className="text-slate-400 text-sm mb-4">发送一封测试邮件验证SMTP配置是否正确</p>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="请输入测试邮箱地址"
              className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleTestEmail}
            disabled={testing}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-all disabled:opacity-50 whitespace-nowrap"
          >
            {testing ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                发送中...
              </span>
            ) : (
              <span className="flex items-center gap-2"><Send className="w-4 h-4" />发送测试</span>
            )}
          </button>
        </div>
        {testResult && (
          <div className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            {testResult.success ? <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
            <span className={`text-sm ${testResult.success ? 'text-green-300' : 'text-red-300'}`}>{testResult.message}</span>
          </div>
        )}
      </div>
    </div>
  );
}